import { Component } from "@theme/component";

const SCROLL_DURATION_MS = 600;
const SCROLL_SETTLE_MS = 300;
const INTERRUPT_EVENTS = ["wheel", "touchstart", "keydown", "pointerdown"];

/**
 * @param {number} progress A value from 0 to 1
 */
const easeInOutCubic = (progress) =>
  progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;

/**
 * Scrolls to the reviews section when the REVIEWS.io rating snippet is clicked.
 * The snippet's default behaviour is kept if the target is missing.
 *
 * The scroll is animated manually, re-reading the target's position on every frame. The native smooth
 * scroll works out its destination once, so content loading above the target mid-scroll (lazy images,
 * the reviews widget) would make it land in the wrong place and then visibly jump back.
 *
 * @extends {Component}
 */
class ProductReviewsRatingComponent extends Component {
  /** @type {(() => void) | null} */
  #stopScroll = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.#handleClick, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#handleClick, { capture: true });
    this.#stopScroll?.();
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const target = document.getElementById(this.getAttribute("scroll-target") ?? "");
    if (!target) return;

    event.preventDefault();
    event.stopPropagation();

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.#scrollTo(target, prefersReducedMotion ? 0 : SCROLL_DURATION_MS);
  };

  /**
   * Moves the page towards the target on every frame, then keeps it aligned briefly in case the layout
   * is still shifting. Stops if the visitor scrolls or interacts with the page.
   *
   * @param {HTMLElement} target
   * @param {number} duration Animation time in ms. 0 jumps straight to the target.
   */
  #scrollTo(target, duration) {
    this.#stopScroll?.();

    const startTop = window.scrollY;
    const startTime = performance.now();
    let frame = 0;

    const getTargetTop = () => {
      const scrollMarginTop = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      return target.getBoundingClientRect().top + window.scrollY - scrollMarginTop;
    };

    const stop = () => {
      cancelAnimationFrame(frame);
      INTERRUPT_EVENTS.forEach((name) => window.removeEventListener(name, stop));
      this.#stopScroll = null;
    };

    /** @param {number} now */
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = duration > 0 ? Math.min(elapsed / duration, 1) : 1;

      window.scrollTo({
        top: startTop + (getTargetTop() - startTop) * easeInOutCubic(progress),
        behavior: "instant",
      });

      if (elapsed < duration + SCROLL_SETTLE_MS) {
        frame = requestAnimationFrame(step);
      } else {
        stop();
      }
    };

    INTERRUPT_EVENTS.forEach((name) => window.addEventListener(name, stop, { passive: true }));
    this.#stopScroll = stop;
    frame = requestAnimationFrame(step);
  }
}

if (!customElements.get("product-reviews-rating-component")) {
  customElements.define("product-reviews-rating-component", ProductReviewsRatingComponent);
}
